import React from "react";

import { Box, Link, makeStyles, Theme, Typography } from "@material-ui/core";

import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CountryList } from "client/lib/apt-merchant";

interface LegalAgreementText {
  part1: string;
  href: string;
  linkText: string;
  part2: string;
}

type AgreementType = "plaid" | "manual";

type CountryToAgreementMap = {
  [key in CountryList]?: LegalAgreementText;
};

type LegalAgreementToCountryMap = Record<AgreementType, CountryToAgreementMap>;

const legalAgreementText: LegalAgreementToCountryMap = {
  plaid: {
    "United States": {
      part1: "By selecting ‘Link bank’, I confirm I agree to the ",
      href: "/ach-authorization-static.html",
      linkText: "ACH Authorization",
      part2:
        " and authorize Afterpay to debit and credit my account pursuant to the merchant agreement.",
    },
    Canada: {
      part1: "By selecting ‘Link bank’, I confirm I agree to the ",
      href: "/pad-authorization-static.html",
      linkText: "PAD Authorization",
      part2:
        " and authorize Afterpay to debit and credit my account pursuant to the merchant agreement.",
    },
  },
  manual: {
    "United States": {
      part1: "By submitting these details, I confirm I agree to the ",
      href: "/ach-authorization-static.html",
      linkText: "ACH Authorization",
      part2:
        " and authorize Afterpay to debit and credit this account pursuant to the merchant agreement.",
    },
    Canada: {
      part1: "By submitting these details, I confirm I agree to the ",
      href: "/pad-authorization-static.html",
      linkText: "PAD Authorization",
      part2:
        " and authorize Afterpay to debit and credit this account pursuant to the merchant agreement.",
    },
    "United Kingdom": {
      part1:
        "By submitting these details, I confirm that I have read and accept the ",
      href: "/clearpay-ddm-authorization.pdf",
      linkText: "direct debit terms and conditions",
      part2:
        " and authorize Clearpay to debit and credit this account pursuant to the merchant agreement.",
    },
  },
};

const defaultAgreement = {
  part1: "",
  href: "",
  linkText: "",
  part2: "",
};

const useStyles = makeStyles((theme: Theme) => {
  const { gray30 } = useNormalizedBrandPaletteValues(theme);

  return {
    root: {
      fontSize: "0.9rem",
      lineHeight: "1.4rem",
      color: gray30,
    },
  };
});

interface BankLegalAgreementProps {
  country?: CountryList;
  agreementType: AgreementType;
}

export const BankLegalAgreement: React.FC<BankLegalAgreementProps> = ({
  agreementType,
  country,
}) => {
  const classes = useStyles();
  if (!country) {
    return null;
  }
  const { part1, href, linkText, part2 } =
    legalAgreementText[agreementType][country] ?? defaultAgreement;

  if (!Boolean(href)) {
    return null;
  }
  return (
    <Typography className={classes.root} variant="body2">
      {part1}
      <Box component="span" color="info.main">
        <Link
          color="inherit"
          href={href}
          rel="noopener"
          target="_blank"
          onClick={() => {
            logAmplitudeEvent(
              "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
              "ach_authorization_link_opened"
            );
          }}
        >
          {linkText}
        </Link>
      </Box>
      {part2}
    </Typography>
  );
};
